import React, { Fragment } from 'react';
import i18n from '../../../../i18n';
import Metric from '../Metric';
import '../KeyMetrics.css';
import { useSelector } from 'react-redux';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
library.add(faCircleNotch);

const MetricsTable = () => {
  const data = useSelector((state) => state.fundData.data);
  const hasLevels = data.filter((x) => x.levels !== null);
  const hasPerfData = hasLevels.length > 0;
  const lenData = data.length;

  function formatPercent(number) {
    return number !== null ? parseFloat(number * 100).toFixed(1) : null;
  }

  function getPerformance(data) {
    return data.map((fund) => {
      if (fund.stdPerformanceDaily) {
        return formatPercent(
          fund.stdPerformanceDaily.slice(-1)[0]?.commonPeriodNAV
        );
      } else {
        return null;
      }
    });
  }

  function getNetFees(data) {
    return data.map((fund) => {
      if (fund.expenseRatio) {
        return (fund.expenseRatio * 100).toFixed(2);
      } else {
        return '-';
      }
    });
  }

  const aumFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

  function getAum(data) {
    return data.map((fund) =>
      fund.aum != null ? aumFormatter.format(fund.aum) : '-'
    );
  }
  function getFeesTooltips(data) {
    return data.map((fund) => {
      if (fund.expenseRatio) {
        let toReturn = `Net Fees: ${(fund.expenseRatio * 100).toFixed(
          2
        )}%<br />Gross Fees: ${(fund.expenseRatioGross * 100).toFixed(2)}%`;
        if (fund.expenseNotes) {
          toReturn = `${toReturn}<br /><br />${fund.expenseNotes}`;
        }
        return toReturn;
      } else {
        return null;
      }
    });
  }

  function getRisk(data) {
    return data.map((fund) => {
      if (fund.stdRisk) {
        return formatPercent(fund.stdRisk.slice(-1)[0]?.commonPeriodNAV);
      } else {
        return '-';
      }
    });
  }

  function getRiskAdj(data) {
    return data.map((fund) => {
      if (!fund.sharpeRatio) {
        return null;
      } else if (fund.sharpeRatio && fund.sharpeRatio?.commonPeriodNAV) {
        return fund.sharpeRatio?.commonPeriodNAV.toFixed(2);
      } else {
        return '-';
      }
    });
  }

  function getIncome(data) {
    return data.map((fund) => {
      if (fund.ttmDivYields) {
        return fund.ttmDivYields.ttmYieldOrdinaryIncome
          ? formatPercent(fund.ttmDivYields.ttmYieldOrdinaryIncome[0])
          : null;
      } else {
        return null;
      }
    });
  }

  const RenderHeader = () => {
    let commonStartDate = null;
    let commonEndDate = null;

    if (data[0].stdPerformanceDaily) {
      commonStartDate = new Date(
        data[0].stdPerformanceDaily[0]?.commonPeriodNAVStart
      ).toLocaleDateString();
      commonEndDate = new Date(
        data[0].stdPerformanceDaily[0]?.commonPeriodNAVEnd
      ).toLocaleDateString();
    }

    const tickerList = data.map((fund) => {
      if (
        fund.type === 'BMK' ||
        fund.type === 'BLEND' ||
        fund.type === 'COD' ||
        fund.type === 'WTMOD'
      ) {
        return (
          <th
            key={fund.name}
            className="textCenter"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '150px',
            }}
          >
            <span data-tip data-for={`_key_metrics_table_header`}>
              {fund.ticker.toUpperCase()}
            </span>
          </th>
        );
      } else {
        return (
          <th key={fund.ticker + '_' + fund.name} className="textCenter">
            {fund.fundPageURL && (
              <a className="tickerLinkFC" href={fund.fundPageURL} target="_new">
                {fund.ticker}
              </a>
            )}
            {!fund.fundPageURL && (
              <a className="tickerLinkFC" href={fund.msUrl} target="_new">
                {fund.ticker}
              </a>
            )}
          </th>
        );
      }
    });

    return (
      <tr>
        <th colSpan="2" className="textLeft">
          {i18n.t('KEY METRICS')} with Common Period of Fund(s){' '}
          {commonStartDate} - {commonEndDate}
        </th>
        {tickerList}
      </tr>
    );
  };

  const RenderRows = () => {
    if (!hasPerfData) {
      return null;
    }

    let asOfDateMonthly = null;
    if (data[0] && data[0].stdPerformanceMonthly) {
      asOfDateMonthly = new Date(
        data[0].stdPerformanceMonthly[0].asOfDate
      ).toLocaleDateString();
    }

    const metrics = [
      {
        displayName: 'Performance',
        description: 'Avg Annual Returns over Common Period (%)',
        values: getPerformance(data),
        append: '%',
        bold: 'max',
      },
      {
        displayName: 'Income',
        description: `Trailing 12M Dividend Yields (%) as of ${asOfDateMonthly}`,
        values: getIncome(data),
        append: '%',
        bold: 'max',
      },
      {
        displayName: 'Risk',
        description: `Avg Annual Volatility as of ${asOfDateMonthly}`,
        values: getRisk(data),
        append: '%',
        bold: 'min',
      },
      {
        displayName: 'Risk-Adj. Returns',
        description: `Avg Annual Sharpe Ratio as of ${asOfDateMonthly}`,
        values: getRiskAdj(data),
        append: '',
        bold: 'max',
      },
      {
        displayName: 'Fees',
        tooltips: getFeesTooltips(data),
        description: `Net Expense Ratio as of ${asOfDateMonthly}`,
        values: getNetFees(data),
        append: '%',
        bold: 'min',
      },
      {
        displayName: 'AUM',
        description: `Assets Under Management(m) as of ${asOfDateMonthly}`,
        values: getAum(data),
        // append: '%',
        bold: 'min',
      },
    ];
    return metrics.map((metric) => {
      return (
        <Metric
          key={metric.displayName}
          displayName={metric.displayName}
          description={metric.description}
          values={metric.values}
          append={metric.append}
          bold={metric.bold}
          hideLine={metric.hideLine}
          tooltips={metric.tooltips}
        />
      );
    });
  };

  const RenderRowLoading = () => {
    return (
      <Fragment>
        <tbody>
          <tr>
            <td
              className="noData"
              colspan={lenData + 2}
              style={{ textAlign: 'center' }}
            >
              {i18n.t('no-data')}
            </td>
          </tr>
        </tbody>
      </Fragment>
    );
  };

  return (
    <div className="table-responsive">
      <table className="table tableFC">
        <thead>{RenderHeader()}</thead>
        {!hasPerfData ? RenderRowLoading() : RenderRows()}
      </table>
    </div>
  );
};

export default MetricsTable;
